import React, {
  KeyboardEvent,
  MouseEvent,
  TouchEvent,
  useCallback,
  useMemo,
  useState,
} from "react";

import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faEllipsisH } from "@fortawesome/pro-regular-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { ItemType } from "antd/es/menu/hooks/useItems";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";

import styles from "./ActivityTypeSelector.module.scss";

const MAX_VISIBLE_ACTIVITY_TYPES = 8;

type Event = KeyboardEvent | MouseEvent | TouchEvent;

interface Props {
  activityTypes: ActivityType[];
  emptyValueMeansAll?: boolean;
  onChange?: (activityTypes: ActivityType[]) => void;
  value: ActivityType[];
}

const ActivityTypeSelector: React.FC<Props> = ({
  activityTypes,
  emptyValueMeansAll,
  onChange,
  value,
}) => {
  const intl = useIntl();

  const [visible, setVisible] = useState(false);
  const visibleActivityTypes = useMemo(
    () =>
      activityTypes
        .slice(
          0,
          activityTypes.length > MAX_VISIBLE_ACTIVITY_TYPES
            ? MAX_VISIBLE_ACTIVITY_TYPES - 1 // to reserve space for dropdown button
            : MAX_VISIBLE_ACTIVITY_TYPES
        )
        .reverse(),
    [activityTypes]
  );
  const hiddenActivityTypes = useMemo(
    () =>
      activityTypes.length > MAX_VISIBLE_ACTIVITY_TYPES
        ? activityTypes.slice(MAX_VISIBLE_ACTIVITY_TYPES - 1)
        : [],
    [activityTypes]
  );

  const selectedActivityTypesIds = useMemo(() => new Set(value.map(({ id }) => id)), [value]);

  const handleClick = useCallback(
    (activityType: ActivityType) => (e: Event) => {
      if (e.altKey && !e.shiftKey) {
        // special behavior - only select this clicked activityType and deselect others
        onChange?.([activityType]);
        return;
      }
      if (e.shiftKey && !e.altKey) {
        // special behavior - select all
        onChange?.(emptyValueMeansAll ? [] : activityTypes);
        return;
      }

      const selectedCount = selectedActivityTypesIds.size;
      let newValue: ActivityType[] = [];
      if (emptyValueMeansAll) {
        if (!selectedCount) {
          // nothing yet selected, then select all but clicked activity type
          newValue = activityTypes.filter(({ id }) => id !== activityType.id);
        } else {
          // something already selected, then add or remove clicked activity type depending on
          // whether it was selected before or not
          newValue = selectedActivityTypesIds.has(activityType.id)
            ? value.filter(({ id }) => id !== activityType.id)
            : [...value, activityType];
          if (newValue.length === activityTypes.length) {
            // apparently we selected all, then reset selection to be empty
            newValue = [];
          }
        }
      } else {
        newValue = selectedActivityTypesIds.has(activityType.id)
          ? value.filter(({ id }) => id !== activityType.id)
          : [...value, activityType];
      }

      onChange?.(newValue);

      // activityListViewAnalytics.clicked(["Activity Filter Icon"], {
      //   activityType: activityType.name,
      // });
    },
    [activityTypes, emptyValueMeansAll, onChange, selectedActivityTypesIds, value]
  );

  const handleMenuItemClick = useCallback(
    (activityType: ActivityType) => (info: MenuInfo) => {
      handleClick(activityType)(info.domEvent);
    },
    [handleClick]
  );

  const items: ItemType[] = useMemo(
    () =>
      hiddenActivityTypes.map((activityType) => {
        const isSelected =
          selectedActivityTypesIds.has(activityType.id) ||
          (emptyValueMeansAll && selectedActivityTypesIds.size === 0);
        return {
          className: cn(styles.menuItem, { [styles.selectedMenuItem]: isSelected }),
          key: activityType.id,
          label: (
            <>
              <div className={styles.activityTypeItem}>
                <ActivityTypeIcon activityType={activityType} size="sm" />
                <span className={styles.textMargin}>{activityType.name}</span>
              </div>
              {isSelected && <FontAwesomeIcon className={styles.checkIcon} icon={faCheck} />}
            </>
          ),
          onClick: handleMenuItemClick(activityType),
        };
      }),
    [emptyValueMeansAll, handleMenuItemClick, hiddenActivityTypes, selectedActivityTypesIds]
  );

  return (
    <div className={styles.container}>
      {hiddenActivityTypes.length > 0 && (
        <Dropdown menu={{ items }} onOpenChange={setVisible} open={visible} trigger={["click"]}>
          <Tooltip
            title={intl.formatMessage({
              id: "activityList.filter.activityType.moreTooltip",
              defaultMessage: "More",
              description:
                "A tooltip for more button of activity type filter on activity list page",
            })}
          >
            <div className={styles.iconContainer}>
              <div className={styles.moreIcon}>
                <FontAwesomeIcon icon={faEllipsisH} />
              </div>
            </div>
          </Tooltip>
        </Dropdown>
      )}
      {visibleActivityTypes.map((activityType) => (
        <div
          className={styles.iconContainer}
          key={activityType.id}
          onClick={handleClick(activityType)}
        >
          <ActivityTypeIcon
            activityType={activityType}
            activityTypeIconClassName={cn({
              [styles.unselected]:
                !selectedActivityTypesIds.has(activityType.id) &&
                ((emptyValueMeansAll && selectedActivityTypesIds.size !== 0) ||
                  !emptyValueMeansAll),
            })}
          />
        </div>
      ))}
    </div>
  );
};

export default ActivityTypeSelector;
